import { useTranslate } from '@ur-solutions/react-hooks'
import { Checkbox } from '@/components_deprecated'
import type {
  FilterChildren} from '@/components_deprecated/FilterPopup';
import {
  FilterPopup,
} from '@/components_deprecated/FilterPopup'
import {
  FilterCheckmarksWrapper,
  Status,
  StatusButton,
  StyledScrollBar,
} from '@/components_deprecated/FilterPopup/components'
import xor from 'lodash/xor'
import React from 'react'
import type {
  InfringementCategoryIdentifiers} from '../consts';
import {
  InfringementSeverities,
} from '../consts'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import {
  useInfringementSeverityFunctions,
  useInfringementCategoryFunctions,
} from '../util'

export const InfringementPopupFilter: React.FC<
  FilterChildren<InfringementsTableFilter>
> = ({ filter, onUpdate }) => {
  const translations = useTranslate({
    archived: 'common.archived',
    status: 'common.status',
    open: 'common.open-alt',
    closed: 'common.closed',

    severity: 'infringements.severity',
    categories: 'common.categories',

    minorInfringement: 'infringements.minor-infringement',
    seriousInfringement: 'infringements.serious-infringement',
    verySeriousInfringement: 'infringements.very-serious-infringement',
    mostSeriousInfringement: 'infringements.most-serious-infringement',
    infringementPoints: ['infringements.n-points', { n: 0 }],
  })
  const severities = Object.keys(InfringementSeverities) as SeverityCategory[]
  const categoryIdentifiers: InfringementCategoryIdentifiers[] = ['B', 'C', 'D']

  function toggleArchived(archived: boolean) {
    return () =>
      onUpdate({
        archived,
      })
  }

  function toggleSeverity(severity: SeverityCategory) {
    return () => {
      if (filter.excludeSeverities.length === 0) {
        onUpdate({
          excludeSeverities: severities.filter(
            s => s !== severity
          ) as SeverityCategory[],
        })
      } else {
        const newExcludeSeverities = xor(filter.excludeSeverities, [
          severity,
        ]) as SeverityCategory[]

        if (newExcludeSeverities.length === severities.length) {
          onUpdate({
            excludeSeverities: [],
          })
        } else {
          onUpdate({
            excludeSeverities: newExcludeSeverities,
          })
        }
      }
    }
  }

  function toggleCategories(category: InfringementCategoryIdentifiers) {
    return () => {
      const isCurrentlyExcluded = filter.excludeCategories
        .map(category => category[0])
        .includes(category)

      if (filter.excludeCategories.length === 0) {
        const allCategoriesExceptSelected = categoryIdentifiers
          .filter(cat => cat !== category)
          .flatMap(cat => getCategoriesFromIdentifier(cat))

        onUpdate({
          excludeCategories: allCategoriesExceptSelected,
        })
      } else {
        if (isCurrentlyExcluded) {
          const newCategories = filter.excludeCategories.filter(
            cat => cat[0] !== category
          )

          const remainingCategories = categoryIdentifiers.filter(
            cat => !newCategories.map(c => c[0]).includes(cat)
          )

          if (remainingCategories.length === categoryIdentifiers.length) {
            onUpdate({
              excludeCategories: [],
            })
          } else {
            onUpdate({
              excludeCategories: newCategories,
            })
          }
        } else {
          const newCategories = [
            ...filter.excludeCategories,
            ...getCategoriesFromIdentifier(category),
          ]

          const excludedIdentifiers = new Set(newCategories.map(cat => cat[0]))

          if (excludedIdentifiers.size === categoryIdentifiers.length) {
            onUpdate({
              excludeCategories: [],
            })
          } else {
            onUpdate({
              excludeCategories: newCategories,
            })
          }
        }
      }
    }
  }

  const { getSeverityTranslation, getSeverityValue } =
    useInfringementSeverityFunctions()

  const { getCategoryTranslation, getCategoriesFromIdentifier } =
    useInfringementCategoryFunctions()

  return (
    <FilterPopup>
      <h4>{translations.status}</h4>
      <Status statusCount={2}>
        <StatusButton
          active={filter.archived === false}
          onClick={toggleArchived(false)}
        >
          {translations.open}/{translations.closed}
        </StatusButton>
        <StatusButton
          active={filter.archived === true}
          onClick={toggleArchived(true)}
        >
          {translations.archived}
        </StatusButton>
      </Status>

      <h4>{translations.severity}</h4>

      <FilterCheckmarksWrapper>
        <StyledScrollBar>
          {severities.map(severity => {
            const checked =
              filter.excludeSeverities.length > 0 &&
              !filter.excludeSeverities?.includes(severity)
            const label = `${getSeverityTranslation(
              severity
            )} (${translations.infringementPoints({
              n: getSeverityValue(severity),
            })})`
            return (
              <Checkbox
                key={severity}
                checked={checked}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleSeverity(severity)}
              />
            )
          })}
        </StyledScrollBar>
      </FilterCheckmarksWrapper>

      <h4>{translations.categories}</h4>

      <FilterCheckmarksWrapper>
        <StyledScrollBar>
          {categoryIdentifiers.map(categoryIdentifier => {
            const checked =
              filter.excludeCategories.length > 0 &&
              !filter.excludeCategories
                .map(category => category[0])
                .includes(categoryIdentifier)
            const label = getCategoryTranslation(categoryIdentifier)
            return (
              <Checkbox
                key={categoryIdentifier}
                checked={checked}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleCategories(categoryIdentifier)}
              />
            )
          })}
        </StyledScrollBar>
      </FilterCheckmarksWrapper>
    </FilterPopup>
  )
}
